import { inject, injectable, interfaces } from 'inversify';
import { ACDLError } from '../../../__legacy__/models/error.model';
import { IUpdateProjectImageSetRequest } from '../../../__legacy__/modules/projectDetails/sub/images/services/projectDetailsImagesApi.service';
import { IMainApiService } from '../../../__legacy__/services/api.service';
import { IApiService } from '../../../__legacy__/services/api.service.base';
import {
  EditAndAcceptAnnotationRequest,
  EditAndRejectAnnotationRequest,
  IAcceptAnnotationRequest,
  ICorrectAndSubmitAnnotationForReviewRequest,
  ICorrectAnnotationRequest,
  IDiscardAnnotationDuringAnnotationRequest,
  IDiscardAnnotationDuringFreeAccessEditRequest,
  IDiscardAnnotationDuringReviewEditRequest,
  IDiscardAnnotationDuringReviewRequest,
  IEditAnnotationRequest,
  IRejectAnnotationRequest,
  IRequestClarificationDuringAnnotationRequest,
  IRequestClarificationDuringFreeAccessEditRequest,
  IRequestClarificationDuringReviewEditRequest,
  IRequestClarificationDuringReviewRequest,
  ISaveAnnotationDraftRequest,
  ISubmitAnnotationForReviewRequest,
} from '../models/Requests';

export interface IFreeAccessAnnotationApiService {
  acceptAsync(request: IAcceptAnnotationRequest): Promise<void | ACDLError>;
  rejectAsync(request: IRejectAnnotationRequest): Promise<void | ACDLError>;

  editAndAcceptAsync(request: EditAndAcceptAnnotationRequest): Promise<void | ACDLError>;
  editAndRejectAsync(request: EditAndRejectAnnotationRequest): Promise<void | ACDLError>;

  discardDuringAnnotationAsync(request: IDiscardAnnotationDuringAnnotationRequest): Promise<void | ACDLError>;
  discardDuringReviewAsync(request: IDiscardAnnotationDuringReviewRequest): Promise<void | ACDLError>;
  discardDuringReviewEditAsync(request: IDiscardAnnotationDuringReviewEditRequest): Promise<void | ACDLError>;
  discardDuringFreeAccessEditAsync(request: IDiscardAnnotationDuringFreeAccessEditRequest): Promise<void | ACDLError>;

  submitForReviewAsync(request: ISubmitAnnotationForReviewRequest): Promise<void | ACDLError>;
  editAsync(request: IEditAnnotationRequest): Promise<void | ACDLError>;
  correctAsync(request: ICorrectAnnotationRequest): Promise<void | ACDLError>;
  correctAndSubmitForReviewAsync(request: ICorrectAndSubmitAnnotationForReviewRequest): Promise<void | ACDLError>;
  saveDraftAsync(request: ISaveAnnotationDraftRequest): Promise<void | ACDLError>;

  requestClarificationDuringAnnotationAsync(request: IRequestClarificationDuringAnnotationRequest): Promise<void | ACDLError>;
  requestClarificationDuringReviewAsync(request: IRequestClarificationDuringReviewRequest): Promise<void | ACDLError>;
  requestClarificationDuringReviewEditAsync(request: IRequestClarificationDuringReviewEditRequest): Promise<void | ACDLError>;
  requestClarificationDuringEditAsync(request: IRequestClarificationDuringFreeAccessEditRequest): Promise<void | ACDLError>;

  updateProjectImageSetAsync(request: IUpdateProjectImageSetRequest): Promise<void | ACDLError>;
}

export namespace IFreeAccessAnnotationApiService {
  export const $: interfaces.ServiceIdentifier<IFreeAccessAnnotationApiService> = Symbol('FREE_ACCESS_ANNOTATION_API_SERVICE');
}

@injectable()
export class FreeAccessAnnotationApiService implements IFreeAccessAnnotationApiService {
  constructor(@inject(IMainApiService.$) private readonly apiService: IApiService) {}

  acceptAsync = async (request: IAcceptAnnotationRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/Accept', request);

  rejectAsync = async (request: IRejectAnnotationRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/Reject', request);

  editAndAcceptAsync = async (request: EditAndAcceptAnnotationRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/EditAndAccept', request);

  editAndRejectAsync = async (request: EditAndRejectAnnotationRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/EditAndReject', request);

  discardDuringAnnotationAsync = async (request: IDiscardAnnotationDuringAnnotationRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/DiscardDuringAnnotation', request);

  discardDuringReviewAsync = async (request: IDiscardAnnotationDuringReviewRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/DiscardDuringReview', request);

  discardDuringReviewEditAsync = async (request: IDiscardAnnotationDuringReviewEditRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/DiscardDuringReviewEdit', request);

  discardDuringFreeAccessEditAsync = async (request: IDiscardAnnotationDuringFreeAccessEditRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/DiscardDuringFreeAccessEdit', request);

  submitForReviewAsync = async (request: ISubmitAnnotationForReviewRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/SubmitForReview', request);

  saveDraftAsync = async (request: ISaveAnnotationDraftRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/SaveDraft', request);

  editAsync = async (request: IEditAnnotationRequest): Promise<void | ACDLError> => await this.apiService.postAsync('/annotation/freeAccess/Edit', request);

  correctAsync = async (request: ICorrectAnnotationRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/Correct', request);

  correctAndSubmitForReviewAsync = async (request: ICorrectAndSubmitAnnotationForReviewRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/CorrectAndSubmitForReview', request);

  requestClarificationDuringAnnotationAsync = async (request: IRequestClarificationDuringAnnotationRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/RequestClarificationDuringAnnotation', request);

  requestClarificationDuringReviewAsync = async (request: IRequestClarificationDuringReviewRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/RequestClarificationDuringReview', request);

  requestClarificationDuringReviewEditAsync = async (request: IRequestClarificationDuringReviewEditRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/RequestClarificationDuringReviewEdit', request);

  requestClarificationDuringEditAsync = async (request: IRequestClarificationDuringFreeAccessEditRequest): Promise<void | ACDLError> =>
    await this.apiService.postAsync('/annotation/freeAccess/RequestClarificationDuringFreeAccessEdit', request);

  updateProjectImageSetAsync = async (request: IUpdateProjectImageSetRequest): Promise<void | ACDLError> => {
    return await this.apiService.postAsync('/Projects/UpdateProjectImagesSet', request);
  };
}
